import type { TaskTemplateDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useScope } from '../lib/scope';
import { fromDateInput, toDateInput } from '../lib/format';
import { OrgUnitPicker } from './OrgUnitPicker';
import { PersonPicker } from './PersonPicker';
import { Button, Dialog, Field, Input, Select, useToast } from './ui';

/**
 * Workflow aus einer Vorlage erzeugen.
 * Die Aufgaben entstehen relativ zu einem Referenzdatum,
 * z. B. "14 Tage vorher Helfer organisieren".
 */
export function TemplateDialog({
  open,
  onClose,
  defaultUnitIds,
  defaultReferenceDate,
}: {
  open: boolean;
  onClose: () => void;
  defaultUnitIds?: string[];
  defaultReferenceDate?: string;
}): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [templateId, setTemplateId] = useState('');
  const [referenceDate, setReferenceDate] = useState('');
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [titlePrefix, setTitlePrefix] = useState('');

  const { data: templates } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => api.get<TaskTemplateDto[]>('/api/task-templates'),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setReferenceDate(toDateInput(defaultReferenceDate ?? new Date().toISOString()));
    setUnitIds(defaultUnitIds ?? (scope.unitId ? [scope.unitId] : []));
    setAssignees([]);
    setTitlePrefix('');
  }, [open, defaultUnitIds, defaultReferenceDate, scope.unitId]);

  const selected = templates?.find((t) => t.id === templateId);

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ createdTaskIds: string[]; skipped: number }>('/api/task-templates/apply', {
        templateId,
        referenceDate: fromDateInput(referenceDate),
        organisationUnitIds: unitIds,
        assigneePersonIds: assignees,
        titlePrefix: titlePrefix || null,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show(
        result.skipped > 0
          ? `${result.createdTaskIds.length} Aufgaben angelegt, ${result.skipped} waren bereits vorhanden`
          : `${result.createdTaskIds.length} Aufgaben angelegt`,
      );
      onClose();
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiError ? error.message : 'Vorlage konnte nicht angewendet werden',
        'error',
      );
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Aufgaben aus Vorlage erzeugen"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => apply.mutate()}
            loading={apply.isPending}
            disabled={!templateId || unitIds.length === 0 || !referenceDate}
          >
            Aufgaben erzeugen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {templates?.length === 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Noch keine Vorlage angelegt – Vorlagen pflegst du unter Verein › Vorlagen.
          </p>
        )}
        <Field label="Vorlage" required>
          {(id) => (
            <Select id={id} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Bitte wählen …</option>
              {(templates ?? []).map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.category ? ` (${template.category})` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Referenzdatum"
          required
          hint="z. B. der Spieltag oder der Veranstaltungstermin"
        >
          {(id) => (
            <Input
              id={id}
              type="date"
              value={referenceDate}
              onChange={(e) => setReferenceDate(e.target.value)}
            />
          )}
        </Field>

        {selected && (
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Es werden {selected.items.length} Aufgaben erzeugt:
            </p>
            <ul className="space-y-1 text-sm text-slate-600">
              {selected.items.map((item) => (
                <li key={item.id} className="flex flex-wrap justify-between gap-x-3 gap-y-1">
                  <span>{item.title}</span>
                  <span className="text-xs text-slate-500">
                    {item.offsetDays === 0
                      ? 'am Tag selbst'
                      : item.offsetDays < 0
                        ? `${Math.abs(item.offsetDays)} Tage vorher`
                        : `${item.offsetDays} Tage danach`}
                  </span>
                  {item.assignees.length > 0 && (
                    <span className="w-full text-xs text-slate-500">
                      Bearbeiter: {item.assignees.map((person) => person.name).join(', ')}
                    </span>
                  )}
                  {(item.checklist.length > 0 || item.attachments.length > 0) && (
                    <span className="w-full text-xs text-slate-500">
                      {[
                        item.checklist.length > 0 ? `☑ ${item.checklist.length} Checklistenpunkt(e)` : null,
                        item.attachments.length > 0 ? `📎 ${item.attachments.length} Anhang/Anhänge` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Field label="Bereich" required>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>

        <Field
          label="Bearbeiter für alle Aufgaben"
          hint="Optional: überschreibt die pro Vorlagenzeile hinterlegten Bearbeiter"
        >
          {(id) => <PersonPicker id={id} value={assignees} onChange={setAssignees} />}
        </Field>

        <Field
          label="Überbezeichnung"
          hint={`Die erzeugten Aufgaben werden darunter gruppiert. Ohne Eingabe wird „${selected?.name ?? 'Vorlage'}“ verwendet.`}
        >
          {(id) => (
            <Input id={id} value={titlePrefix} onChange={(e) => setTitlePrefix(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
